// utils/surveyChoices.ts
// Helpers that keep choice-based survey answers both correctly aggregated and
// human-readable after the survey creator edits or removes choices.
//
// Answers are stored as the SurveyJS choice *value* (so survey-analytics, which
// aggregates by value, counts them). To avoid losing the human-readable label
// when a choice is later removed/renamed, each response also carries a
// value -> text snapshot taken at submit time; reporting re-applies it.

const CHOICE_TYPES = ['radiogroup', 'dropdown', 'checkbox', 'tagbox']
// Values SurveyJS handles specially; they are not real choices.
const SPECIAL_VALUES = new Set(['other', 'none'])

// Reserved key used to stash the value -> text snapshot inside a response's data.
// Not a question name, so survey-analytics / Tabulator ignore it.
export const CHOICE_LABELS_KEY = '__choiceLabels'

/**
 * Builds a { questionName: { value: text } } snapshot of every choice question's
 * options at submit time. Stored alongside the response so a choice that is later
 * removed or renamed can still be displayed with the label the respondent saw.
 */
export function buildChoiceLabelSnapshot(
    surveyModel: any
): Record<string, Record<string, string>> {
    const labels: Record<string, Record<string, string>> = {}
    surveyModel.getAllQuestions().forEach((q: any) => {
        if (!CHOICE_TYPES.includes(q.getType?.())) return
        const map: Record<string, string> = {}
        ;(q.choices || []).forEach((c: any) => {
            map[String(c.value)] = c.text ?? String(c.value)
        })
        if (Object.keys(map).length) labels[q.name] = map
    })
    return labels
}

/**
 * Re-adds choices that respondents selected but that are no longer in the survey
 * definition (e.g. the creator removed them after responses came in), so those
 * answers still render in reporting instead of being dropped from charts or shown
 * as a raw value. Labels come from each response's CHOICE_LABELS_KEY snapshot; if
 * a snapshot is unavailable (responses collected before snapshots existed), the
 * stored value is used as a fallback label.
 *
 * `ItemValue` is passed in so this helper stays free of a static survey-core
 * import — the reporting components load survey-core lazily on the client.
 */
export function reconcileRemovedChoices(
    survey: any,
    responses: Array<Record<string, any>>,
    ItemValue: any
): void {
    if (!Array.isArray(responses) || !responses.length) return
    survey.getAllQuestions().forEach((q: any) => {
        if (!CHOICE_TYPES.includes(q.getType?.())) return
        const known = new Set((q.choices || []).map((c: any) => String(c.value)))
        responses.forEach((resp) => {
            const answer = resp?.[q.name]
            if (answer === undefined || answer === null) return
            const values = Array.isArray(answer) ? answer : [answer]
            values.forEach((val) => {
                if (val === undefined || val === null) return
                const key = String(val)
                if (known.has(key) || SPECIAL_VALUES.has(key)) return
                const label = resp?.[CHOICE_LABELS_KEY]?.[q.name]?.[key] ?? key
                q.choices.push(new ItemValue(val, label))
                known.add(key)
            })
        })
    })
}
